import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { classNames } from '../utils/UNSAFE_classNames';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { StyledComponentProps } from '../utils/UNSAFE_typeUtils';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';

const labelStyles = {
  base: cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-font-size)',
    fontWeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-font-weight)',
    lineHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-line-height)',
    // TODO: Evaluate usage of Text Component instead of CSS (we might not be able to as we might need label element)
    wordBreak: 'break-word'
  }}`,
  startBase: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-color)',
    // TODO: Reevaluate which component should handle the text align
    textAlign: 'var(--oj-c-PRIVATE-DO-NOT-USE-form-layout-start-edge-label-text-align)',
    width: '100%'
  }}`,
  topBase: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-color)'
  }}`,
  insideBase: cssProps`${{
    display: 'block',
    boxSizing: 'border-box',
    // TODO: Reevaluate which component should handle the padding
    paddingInlineEnd: 'calc(2 * var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-to-edge-padding))',
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-color)',
    maxWidth: '100%'
  }}`,
  insideReadonlyInEnabledForm: cssProps`${{
    insetInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-to-edge-padding)',
    top: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-position-top)',
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-font-size)',
    fontWeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-font-weight)',
    lineHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-line-height)',
    transitionProperty: 'none',
    transform: 'none',
    backgroundColor: 'transparent',
    position: 'absolute',
    marginBottom: 'var(--oj-c-PRIVATE-DO-NOT-USE-form-layout-top-edge-label-to-value-padding)',
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
    overflowY: 'hidden',
    textOverflow: 'ellipsis'
  }}`,
  insideReadonlyNotInEnabledForm: cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-font-size)',
    fontWeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-font-weight)',
    lineHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-line-height)'
  }}`,
  insideNonReadonly: cssProps`${{
    position: 'absolute',
    insetInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-to-edge-padding)',
    top: 'calc(var(--oj-c-PRIVATE-DO-NOT-USE-text-field-inside-edge-height)/ 2)',
    transform: 'translateY(-50%)',
    transitionProperty: 'all',
    transitionDuration: '.25s',
    transitionTimingFunction: 'ease',
    transitionDelay: '.1s',
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
    overflowY: 'hidden',
    textOverflow: 'ellipsis'
  }}`,
  insideNonReadonlyHasValue: cssProps`${{
    transitionProperty: 'top, font-weight, font-size, transform',
    transitionDuration: '.25s',
    transitionTimingFunction: 'ease',
    transitionDelay: '.1s',
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-font-size)',
    fontWeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-font-weight)',
    lineHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-line-height)',
    transform: 'none',
    top: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-position-top)'
  }}`,
  insideDisabled: cssProps`${{
    transitionProperty: 'none',
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-label-color-disabled)'
  }}`,
  insideFocused: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-inside-edge-label-color-focus)'
  }}`,
  insideError: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-danger-3)'
  }}`,
  insideWarning: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-warning-3)'
  }}`,

  noWrap: cssProps`${{
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
    overflowY: 'hidden',
    textOverflow: 'ellipsis'
  }}`
};

type StyleProps = Pick<Props, 'variant'> & {
  hasValue?: boolean;
  isFocused?: boolean;
};
type StyledLabelProps = StyledComponentProps<'label', StyleProps>;

const StyledLabel = ({
  hasValue,
  readonly,
  disabled,
  isFocused,
  variant = 'inside',
  ...props
}: StyledLabelProps) => {
  const { isFormLayout, isReadonly: isReadonlyForm, labelWrapping } = useFormContext();

  const cssClassNames =
    variant === 'inside' || variant === 'insideError' || variant === 'insideWarning'
      ? classNames([
          labelStyles.base,
          labelStyles.insideBase,
          readonly &&
            (isFormLayout && !isReadonlyForm
              ? labelStyles.insideReadonlyInEnabledForm
              : labelStyles.insideReadonlyNotInEnabledForm),
          !readonly && labelStyles.insideNonReadonly,
          !readonly && (hasValue || isFocused) && labelStyles.insideNonReadonlyHasValue,
          !readonly && isFocused && labelStyles.insideFocused,
          disabled && labelStyles.insideDisabled,
          !disabled &&
            !readonly &&
            (variant === 'insideError' || variant === 'insideWarning') &&
            labelStyles[variant]
        ])
      : variant === 'start'
      ? classNames([
          labelStyles.base,
          labelStyles.startBase,
          labelWrapping === 'truncate' && labelStyles.noWrap
        ])
      : variant === 'top'
      ? classNames([
          labelStyles.base,
          labelStyles.topBase,
          labelWrapping === 'truncate' && labelStyles.noWrap
        ])
      : undefined;
  // TODO: We need to decide whether to standardize on 'class' or 'className'.  Otherwise, we'll
  // run into issues destructuring style interpolations, for example having to concatenate both.
  return <label {...props} class={cssClassNames} />;
};

type Props = {
  /**
   * The text to be used for the label
   */
  children?: string;
  /**
   * The id to set on the label
   */
  id?: string;
  /**
   * The id of the form control for which this label will be used
   */
  forId?: string;
  /**
   * Determines how the label is going to be styled
   */
  variant?: 'inside' | 'start' | 'top' | 'insideError' | 'insideWarning';
};

export const Label = ({ forId, ...props }: Props) => {
  const { hasValue, isDisabled, isFocused, isReadonly } = useFormFieldContext();
  return (
    <StyledLabel
      for={forId}
      hasValue={hasValue}
      disabled={isDisabled}
      isFocused={isFocused}
      readonly={isReadonly}
      {...props}
    />
  );
};
